class ContactDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this;
    this.floatingButton = document.getElementById('contact-floating-button');
    this.openButton = this.floatingButton?.querySelector('.contact-btn');
    this.closeButton = this.querySelector('#contact-close');
    this.lastScrollY = window.scrollY;
    this.scrollThreshold = 200;
    this.currentPage = 'main';
    this.pageHistory = [];
    this.knowledgeBase = this.buildKnowledgeBase();
    this.init();
  }

  init() {
    this.showButton();
    this.setupEventListeners();
    this.setupGlobalTrigger(); 
    this.initSmartBot(); 
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    
    // Auto-open si formulaire soumis
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('contact_posted') === 'true') {
      this.open();
      this.navigateTo('message');
      const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
      window.history.replaceState({path: newUrl}, '', newUrl);
    }
  }

  // ===============================================
  // 1. TRIGGER GLOBAL SÉCURISÉ
  // ===============================================
  setupGlobalTrigger() {
    document.addEventListener('click', (e) => {
      // Si l'utilisateur veut ouvrir dans un nouvel onglet (CTRL/CMD + Click), on le laisse faire
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;

      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      
      // On ouvre le tiroir SEULEMENT si c'est un lien interne vers contact
      // Et on ignore les liens mailto:, tel:, ou javascript:
      if (href && href.includes('contact') && !href.match(/^(mailto|tel|javascript):/)) {
        e.preventDefault(); 
        this.open();        
      }
    });
  }

  setupEventListeners() {
    if (this.openButton) this.openButton.addEventListener('click', () => this.open());
    if (this.closeButton) this.closeButton.addEventListener('click', () => this.close());
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') this.close();
    });

    this.addEventListener('click', (e) => {
      if (e.target === this.drawer) {
        this.close();
        return;
      }
      const navBtn = e.target.closest('[data-navigate]');
      if (navBtn) this.navigateTo(navBtn.getAttribute('data-navigate'));
      
      const backBtn = e.target.closest('[data-back]');
      if (backBtn) this.navigateBack();
    });
  }

  navigateTo(pageName) {
    const currentPageEl = this.querySelector('.contact-page.active');
    const targetPageEl = this.querySelector(`[data-page="${pageName}"]`);
    if (!targetPageEl || currentPageEl === targetPageEl) return;
    this.pageHistory.push(this.currentPage);
    currentPageEl.classList.add('sliding-out');
    setTimeout(() => {
      currentPageEl.classList.remove('active', 'sliding-out');
      targetPageEl.classList.add('active');
      this.currentPage = pageName;
    }, 200);
  }

  navigateBack() {
    if (this.pageHistory.length === 0) {
      this.close();
      return;
    }
    const previousPage = this.pageHistory.pop();
    const currentPageEl = this.querySelector('.contact-page.active');
    const targetPageEl = this.querySelector(`[data-page="${previousPage}"]`);
    if (!targetPageEl) return;
    currentPageEl.classList.remove('active');
    targetPageEl.classList.add('active');
    this.currentPage = previousPage;
  }

  buildKnowledgeBase() {
    const detailPages = this.querySelectorAll('.contact-detail[data-keywords]');
    const knowledgeBase = [];
    detailPages.forEach(page => {
      const pageName = page.closest('[data-page]').getAttribute('data-page');
      const keywords = page.getAttribute('data-keywords').split(',').map(k => k.trim().toLowerCase());
      const content = page.querySelector('.wysiwyg');
      if (content) {
        knowledgeBase.push({ page: pageName, keywords: keywords });
      }
    });
    return knowledgeBase;
  }

  initSmartBot() {
    this.chatInput = this.querySelector('#smart-bot-input');
    this.chatSend = this.querySelector('#smart-bot-send');
    this.chatHistory = this.querySelector('#chat-history');
    if(!this.chatInput) return;

    this.chatSend.addEventListener('click', () => this.handleUserMessage());
    this.chatInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this.handleUserMessage();
    });
  }

  cleanText(sentence) {
    return sentence.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s]/g, " "); 
  }

  getCustomAnswer(cleanText) {
    const brain = [
      { keywords: ['enceinte', 'grossesse', 'pregnant'], answer: "Par précaution, nous déconseillons l'utilisation de courants (micro-courants) durant la grossesse. Les outils mécaniques (Gua Sha) sont cependant autorisés." },
      { keywords: ['boutique', 'magasin', 'shop', 'store', 'paris', 'address'], answer: "Retrouvez nos adresses et horaires dans la rubrique 'Boutiques' du menu principal." }
    ];
    for (const entry of brain) {
      if (entry.keywords.some(keyword => cleanText.includes(keyword))) return entry.answer;
    }
    return null;
  }

  checkRedirection(cleanText) {
    const rules = [
      { keys: ['livraison', 'colis', 'suivi', 'shipping', 'track', 'order'], page: 'shipping' },
      { keys: ['retour', 'remboursement', 'return', 'refund'], page: 'shipping' },
      { keys: ['pro', 'b2b', 'wholesale', 'revendeur'], page: 'b2b' },
      { keys: ['collab', 'partenariat', 'presse', 'press'], page: 'collaboration' },
      { keys: ['recrutement', 'job', 'stage', 'career', 'work'], page: 'careers' }
    ];
    for (const rule of rules) {
      if (rule.keys.some(k => cleanText.includes(k))) return rule.page;
    }
    return null;
  }

  extractSearchKeywords(cleanText) {
    const stopWords = ['le','la','les','un','une','des','est','sont','je','tu','il','nous','vous','mon','ton','the','a','is','are','i','you','we','for','with'];
    return cleanText.split(/\s+/).filter(w => w.length > 2 && !stopWords.includes(w)).join(" ");
  }

  handleUserMessage() {
    const rawText = this.chatInput.value.trim();
    if (!rawText) return;
    this.addBubble(rawText, 'user');
    this.chatInput.value = '';
    this.addBubble("...", 'bot', true);
    const cleaned = this.cleanText(rawText);

    const customResponse = this.getCustomAnswer(cleaned);
    if (customResponse) {
      setTimeout(() => {
        this.removeLoadingBubble();
        this.addBubble(customResponse, 'bot');
        this.addContactFallbackBtn();
      }, 1000);
      return; 
    } 

    let searchQuery = this.extractSearchKeywords(cleaned);
    if(searchQuery.length < 2) searchQuery = cleaned;

    setTimeout(() => {
        fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchQuery)}&resources[type]=product,page&resources[limit]=3`)
          .then(res => res.json())
          .then(data => {
            const r = data.resources.results;
            const total = (r.products?.length || 0) + (r.pages?.length || 0);
            if (total > 0) {
              this.removeLoadingBubble();
              let html = "Voici ce que j'ai trouvé :<br>";
              const buildItem = (item, type) => `<a href="${item.url}" class="chat-result-item"><div class="chat-result-info"><span class="chat-result-title">${item.title}</span><span class="chat-result-type">${type}</span></div></a>`;
              if(r.products) r.products.forEach(p => html += buildItem(p, 'Produit'));
              if(r.pages) r.pages.forEach(p => html += buildItem(p, 'Info'));
              this.addBubble(html, 'bot');
              this.addContactFallbackBtn();
            } else {
              this.handleRedirectionFallback(cleaned);
            }
          })
          .catch(() => { this.handleRedirectionFallback(cleaned); });
    }, 1200);
  }

  handleRedirectionFallback(cleanedText) {
      const redirectPage = this.checkRedirection(cleanedText);
      this.removeLoadingBubble();
      if (redirectPage) {
         this.addBubble("Je vous redirige vers la section concernée...", 'bot');
         setTimeout(() => {
           this.navigateTo(redirectPage);
         }, 1500);
      } else {
         this.addBubble("Je n'ai pas trouvé de réponse immédiate. Voulez-vous envoyer un message à l'équipe ?", 'bot');
         this.addContactFallbackBtn();
      }
  }

  addBubble(html, type, isLoading = false) {
    const div = document.createElement('div');
    div.className = `chat-bubble ${type} ${isLoading ? 'loading-msg' : ''}`;
    div.innerHTML = html;
    this.chatHistory.appendChild(div);
    this.chatHistory.scrollTop = this.chatHistory.scrollHeight;
  }

  removeLoadingBubble() {
    const loading = this.chatHistory.querySelector('.loading-msg');
    if(loading) loading.remove();
  }

  addContactFallbackBtn() {
    if(this.chatHistory.lastElementChild.querySelector('.chat-fallback-btn')) return;
    const btn = document.createElement('button');
    btn.className = 'chat-fallback-btn';
    btn.innerText = "✉️ Envoyer un message";
    btn.onclick = () => this.navigateTo('message'); 
    const wrapper = document.createElement('div');
    wrapper.style.marginTop = "5px";
    wrapper.appendChild(btn);
    this.chatHistory.appendChild(wrapper);
    this.chatHistory.scrollTop = this.chatHistory.scrollHeight;
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    if (currentScrollY <= this.scrollThreshold) this.showButton();
    else currentScrollY < this.lastScrollY ? this.showButton() : this.hideButton();
    this.lastScrollY = currentScrollY;
  }

  showButton() { this.floatingButton?.classList.add('visible'); }
  hideButton() { this.floatingButton?.classList.remove('visible'); }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.resetToMainPage();
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(() => { this.resetToMainPage(); }, 500);
  }

  resetToMainPage() {
    if (this.querySelector('[data-page="message"].active') && window.location.search.includes('contact_posted=true')) return;
    this.querySelectorAll('.contact-page').forEach(page => page.classList.remove('active', 'sliding-out'));
    const mainPage = this.querySelector('[data-page="main"]');
    if (mainPage) mainPage.classList.add('active');
    this.currentPage = 'main';
    this.pageHistory = [];
  }
}
customElements.define('contact-drawer', ContactDrawer);